import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Plus, FolderPlus, Trash2, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/history")({
  component: HistoryPage,
});

type Row = { id: string; title: string; updated_at: string; kind: string | null; folder_id: string | null };
type Folder = { id: string; name: string };

function HistoryPage() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.resolvedLanguage?.startsWith("en") ? "en-US" : "it-IT";
  const KIND_LABELS: Record<string, { label: string; cls: string }> = {
    ask: { label: t("pages.history.kindAsk"), cls: "bg-rose-500/10 text-rose-300 border-rose-500/25" },
    draft: { label: t("pages.history.kindDraft"), cls: "bg-sky-500/10 text-sky-300 border-sky-500/25" },
    compare: { label: t("pages.history.kindCompare"), cls: "bg-emerald-500/10 text-emerald-300 border-emerald-500/25" },
  };

  const [rows, setRows] = useState<Row[]>([]);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [search, setSearch] = useState("");
  const [kindFilter, setKindFilter] = useState<"all" | "ask" | "draft" | "compare">("all");
  const [activeFolder, setActiveFolder] = useState<string | "all" | "root">("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const [{ data: r }, { data: f }] = await Promise.all([
      supabase.from("research_sessions").select("id, title, updated_at, kind, folder_id").order("updated_at", { ascending: false }),
      supabase.from("research_folders").select("id, name").order("created_at"),
    ]);
    setRows((r as any) ?? []);
    setFolders((f as any) ?? []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const createFolder = async () => {
    const name = prompt(t("pages.history.folderNamePrompt"));
    if (!name) return;
    const { data: u } = await supabase.auth.getUser();
    if (!u.user) return;
    const { error } = await supabase.from("research_folders").insert({ owner_id: u.user.id, name });
    if (error) return toast.error(error.message);
    load();
  };

  const removeRow = async (id: string) => {
    if (!confirm(t("pages.history.deleteConfirm"))) return;
    await supabase.from("research_sessions").delete().eq("id", id);
    toast.success(t("pages.common.deleted"));
    load();
  };

  const filtered = useMemo(() => rows.filter((r) =>
    (kindFilter === "all" || (r.kind ?? "ask") === kindFilter) &&
    (activeFolder === "all" || (activeFolder === "root" ? !r.folder_id : r.folder_id === activeFolder)) &&
    (!search || r.title.toLowerCase().includes(search.toLowerCase()))
  ), [rows, kindFilter, activeFolder, search]);

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-6">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{t("pages.history.eyebrow")}</p>
          <h1 className="font-display text-3xl font-semibold">{t("pages.history.title", { n: rows.length })}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("pages.history.description")}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={createFolder} className="gap-1.5">
            <FolderPlus className="h-4 w-4" /> {t("pages.history.newFolder")}
          </Button>
          <Button asChild size="sm" className="gap-1.5">
            <Link to="/app/research"><Plus className="h-4 w-4" /> {t("pages.history.newSearch")}</Link>
          </Button>
        </div>
      </header>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("pages.common.search")} className="h-9 w-64 pl-9" />
        </div>
        <div className="flex gap-1">
          {(["all", "ask", "draft", "compare"] as const).map((k) => (
            <button key={k} onClick={() => setKindFilter(k)}
              className={cn("rounded-full border px-3 py-1.5 text-xs font-medium transition",
                kindFilter === k ? "border-border-strong bg-primary text-primary-foreground" : "border-border text-foreground hover:text-foreground")}>
              {k === "all" ? t("pages.history.filterAll") : KIND_LABELS[k].label}
            </button>
          ))}
        </div>
      </div>

      {folders.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-1.5 text-xs">
          <Chip active={activeFolder === "all"} onClick={() => setActiveFolder("all")}>{t("pages.history.allFolders")}</Chip>
          <Chip active={activeFolder === "root"} onClick={() => setActiveFolder("root")}>{t("pages.history.noFolder")}</Chip>
          {folders.map((f) => (
            <Chip key={f.id} active={activeFolder === f.id} onClick={() => setActiveFolder(f.id)}>{f.name}</Chip>
          ))}
        </div>
      )}

      {loading ? (
        <div className="space-y-2">{[0,1,2].map((i) => <div key={i} className="h-14 animate-pulse rounded-lg bg-muted/30" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border/60 bg-card/40 p-12 text-center">
          <Search className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 text-sm text-muted-foreground">{t("pages.history.noResults")}</p>
          <Button asChild size="sm" className="mt-4">
            <Link to="/app/research">{t("pages.history.startFirst")}</Link>
          </Button>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-border/60 bg-card/40">
          <table className="w-full text-sm">
            <thead className="border-b border-border/60 bg-muted/20 text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
              <tr>
                <th className="px-4 py-2.5 text-left font-medium">{t("pages.history.colName")}</th>
                <th className="px-4 py-2.5 text-left font-medium">{t("pages.history.colDate")}</th>
                <th className="px-4 py-2.5 text-left font-medium">{t("pages.history.colKind")}</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const k = KIND_LABELS[r.kind ?? "ask"] ?? KIND_LABELS.ask;
                return (
                  <tr key={r.id} className="group border-t border-border/40 transition hover:bg-muted/20">
                    <td className="px-4 py-3">
                      <Link to="/app/research" className="block max-w-md truncate font-medium hover:underline">{r.title}</Link>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{new Date(r.updated_at).toLocaleDateString(dateLocale, { day: "2-digit", month: "short", year: "numeric" })}</td>
                    <td className="px-4 py-3"><span className={cn("inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold", k.cls)}>{k.label}</span></td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1 opacity-0 transition group-hover:opacity-100">
                        <button onClick={() => removeRow(r.id)} className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-destructive" title={t("pages.history.delete")}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                        <Link to="/app/research" className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground" title={t("pages.history.open")}>
                          <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} className={cn("rounded-full border px-2.5 py-1 transition",
      active ? "border-border-strong bg-primary text-primary-foreground" : "border-border text-foreground hover:text-foreground")}>
      {children}
    </button>
  );
}
